import { sortConversations } from '@sd/client-core';
import { LIMITS } from '@sd/contracts';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { installedApp, useBackButton } from '../ui/backButton';
import { useKeyboardLift } from '../ui/keyboard';
import { enterSends } from '../ui/keys';
import { closeTopLayer, useConfirm, useEscape, useOpenLayers, useToast } from '../ui/overlays';
import { ChatPane, scannerProblem } from './ChatPane';
import { useAppEnv, useClient, useClientState } from './context';
import { ConversationList } from './ConversationList';
import { errorMessage } from '../errors';
import { conversationName, sizeLimitText } from './copy';
import { DetailPanel } from './DetailPanel';
import { useShellLayout, usePosture, useViewportWidth, type MobileView } from './layout';
import { PeopleModal } from './modals/PeopleModal';
import { PreviewModal } from './modals/PreviewModal';
import { SearchModal } from './modals/SearchModal';
import { ServersModal } from './modals/ServersModal';
import { Rail } from './Rail';
import { ComposerActionsContext, ShellContext, type ComposerActions, type ShellApi } from './shell';
import { Titlebar } from './Titlebar';

// Dialogs opened now and then come when first opened (the settings bring the photo editor and the phone QR code).
const InviteModal = lazy(() => import('./modals/InviteModal').then((m) => ({ default: m.InviteModal })));
const SettingsModal = lazy(() => import('./modals/SettingsModal').then((m) => ({ default: m.SettingsModal })));
const ForwardModal = lazy(() => import('./modals/ForwardModal').then((m) => ({ default: m.ForwardModal })));

export interface MessengerScreenProps {
  /** First panel on phones (< 600 px). */
  initialView?: MobileView;
  /** Accept `?posture=span-h|span-v` and `?display=standalone` (dev/fixture only). */
  allowPostureParam?: boolean;
}

/** How long after a first back press at the list a second one asks to close the app. */
const EXIT_WINDOW_MS = 2000;
/** Conversations loaded ahead after the start, and how long after the first sync (out of the start's way). */
const PREFETCH_CONVERSATIONS = 6;
const PREFETCH_DELAY_MS = 1500;

/** Closes the installed app. A window the page did not open closes only while its history has one entry. */
function closeApp(onRefused: () => void) {
  window.close();
  window.setTimeout(() => {
    if (!window.closed) onRefused();
  }, 400);
}

/** The messenger screen of design B (`#scr-messenger`). */
export function MessengerScreen({ initialView = 'list', allowPostureParam = false }: MessengerScreenProps) {
  const env = useAppEnv();
  const client = useClient();
  const toast = useToast();
  const width = useViewportWidth();
  const posture = usePosture(allowPostureParam);
  const layout = useShellLayout(width, posture, initialView);
  const { setView } = layout;
  const status = useClientState((s) => s.status);
  // Whether any conversation is listed, not the list itself: a screen-wide subscription redrew everything below it at
  // every new message or unread count. The opening pick reads the list when it runs.
  const listed = useClientState((s) => Object.keys(s.conversations).length > 0);
  const activeId = useClientState((s) => s.activeConversationId);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const editPrev = useRef('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const insertRef = useRef<(text: string) => void>(() => {});

  const openConversation = useCallback(
    (conversationId: string, options?: { messageId?: string }) => {
      setReplyTo(null);
      setEditing(null);
      void client.openConversation(conversationId, options);
      setView('chat');
    },
    [client, setView],
  );

  // A conversation asked for from a notification is in front, on phones too (where the list would cover it).
  const shown = useClientState((s) => s.shown);
  useEffect(() => {
    if (!shown) return;
    setReplyTo(null);
    setEditing(null);
    setView('chat');
    client.clearShown(shown.nonce);
  }, [shown, setView, client]);

  // Select a conversation on wide layouts (the design always shows one).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || status !== 'ready' || activeId) return;
    const list = Object.values(client.state.conversations);
    if (!list.length) return;
    if (layout.single && initialView === 'list') return;
    autoOpened.current = true;
    const pins = list.filter((c) => c.my.pinned).sort((a, b) => conversationName(a).localeCompare(conversationName(b), 'ko'));
    const first = pins[0] ?? sortConversations(list)[0];
    if (first) {
      void client.openConversation(first.id);
      if (initialView !== 'list') setView(initialView);
    }
  }, [status, activeId, listed, layout.single, setView, client, initialView]);

  // Once the list is up and the first sync done, the conversations most likely opened next (unread first, then the
  // latest) load their newest messages quietly: tapping one shows it at once instead of "메시지를 불러오는 중…".
  const online = useClientState((s) => s.connection === 'online');
  const prefetched = useRef(false);
  useEffect(() => {
    if (prefetched.current || status !== 'ready' || !online) return undefined;
    const timer = window.setTimeout(() => {
      prefetched.current = true;
      const list = sortConversations(Object.values(client.state.conversations)).filter((c) => c.id !== client.state.activeConversationId && c.my.state === 'active');
      const likely = [...list.filter((c) => c.unreadCount > 0), ...list.filter((c) => !c.unreadCount)].slice(0, PREFETCH_CONVERSATIONS);
      void client.prefetch(likely.map((c) => c.id));
    }, PREFETCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [status, online, client]);

  // Client notices → toasts.
  useEffect(
    () =>
      client.onNotice((notice) => {
        switch (notice.kind) {
          case 'sync.done':
            toast(notice.flushed ? '동기화를 마쳤어요. 대기 중이던 메시지를 보내는 중이에요' : '동기화를 마쳤어요', { icon: 'checkc' });
            break;
          case 'upload.rejected': {
            const limits = client.state.serverInfo?.limits ?? LIMITS;
            toast(
              notice.reason === 'too_many'
                ? `한 번에 ${limits.maxFilesPerMessage}개까지 보낼 수 있어요. ${notice.name}개는 제외했어요`
                : notice.reason === 'too_large'
                  ? `${notice.name}: 파일당 최대 ${sizeLimitText(limits.maxFileBytes)}예요`
                  : notice.reason === 'not_allowed'
                    ? `${notice.name}: 실행 파일은 보낼 수 없어요`
                    : notice.reason === 'infected'
                      ? `${notice.name}: 악성코드가 발견돼 보낼 수 없어요`
                      : `${notice.name}: 업로드하지 못했어요`,
            );
            break;
          }
          case 'scheduled.failed':
            toast(`예약한 메시지를 보내지 못했어요 · ${errorMessage(notice.code)}`, { icon: 'alert' });
            break;
          case 'upload.sent_when_ready': {
            const held = notice.held.length > 1 ? `${notice.held[0]} 외 ${notice.held.length - 1}개` : notice.held[0];
            if (!held) toast(`검사가 끝나 파일 ${notice.sent}개를 보냈어요`, { icon: 'checkc' });
            else toast(`${notice.sent ? `파일 ${notice.sent}개를 보냈어요. ` : ''}${held}: 악성코드 검사를 할 수 없어 보내지 않았어요. 확인한 뒤 ‘파일 보내기’로 보내 주세요`);
            break;
          }
          case 'download.done':
            toast(`${notice.name} 저장 완료`, { action: '폴더 열기', onAction: () => void client.showInFolder(notice.fileId) });
            break;
          case 'download.failed':
            toast(`${notice.name}: 다운로드하지 못했어요${notice.code ? ` · ${errorMessage(notice.code)}` : ''}`);
            break;
          case 'storage.unavailable':
            toast('이 PC에서 안전한 비밀 저장소를 찾지 못해 대기 중인 메시지를 저장하지 않아요');
            break;
          case 'error':
            toast(errorMessage(notice.code));
            break;
          default:
            break;
        }
      }),
    [client, toast],
  );

  // Owners and admins hear about a stopped virus scanner on any screen, the conversation list of a phone too;
  // the chat pane keeps a banner until it works again.
  const scanner = useClientState((s) => (s.me?.role === 'owner' || s.me?.role === 'admin' ? scannerProblem(s.serverInfo?.scanner) : null));
  const toldScanner = useRef<string | null>(null);
  useEffect(() => {
    if (scanner && scanner !== toldScanner.current) toast(scanner, { icon: 'alert' });
    toldScanner.current = scanner;
  }, [scanner, toast]);

  // Ctrl/⌘K opens search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        env.openModal({ kind: 'search' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [env]);

  useEscape(() => layout.toggleDetail(false), !layout.wide && !layout.single && layout.detailOpen);

  // A phone's back button closes what is open (a menu, a dialog, the info panel), then goes back a panel. In the
  // installed app it does not close the app at the list: a first press says how, a second one asks. A browser tab
  // leaves the page there, as browsers do.
  const confirm = useConfirm();
  const layers = useOpenLayers();
  const touch = env.platform.kind === 'web' && !enterSends();
  const app = touch && (installedApp() || (allowPostureParam && new URLSearchParams(window.location.search).get('display') === 'standalone'));
  const exitArmed = useRef(0);
  useBackButton(touch && (layers > 0 || (layout.single && layout.view !== 'list') || app), () => {
    if (closeTopLayer()) return;
    if (layout.single && layout.view !== 'list') {
      layout.setView(layout.view === 'detail' ? 'chat' : 'list');
      return;
    }
    if (!app) return;
    if (Date.now() < exitArmed.current) {
      exitArmed.current = 0;
      void confirm({ title: '앱을 종료할까요?', body: '메시지는 그대로 남아 있어요. 홈 화면에서 다시 열 수 있어요.', ok: '종료' }).then((ok) => {
        if (ok) closeApp(() => toast('앱을 닫지 못했어요. 홈 버튼으로 나가 주세요'));
      });
      return;
    }
    exitArmed.current = Date.now() + EXIT_WINDOW_MS;
    toast('뒤로 버튼을 한 번 더 누르면 앱을 종료할 수 있어요', { ms: EXIT_WINDOW_MS });
  });

  const actions = useMemo<ComposerActions & Pick<ShellApi['composer'], 'cancel' | 'registerInsert'>>(
    () => ({
      setReplyTo: (id) => {
        setEditing(null);
        setReplyTo(id);
      },
      startEdit: (id) => {
        editPrev.current = inputRef.current?.value ?? '';
        setReplyTo(null);
        setEditing(id);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      },
      cancel: () => {
        setReplyTo(null);
        setEditing(null);
      },
      insert: (text) => insertRef.current(text),
      registerInsert: (fn) => {
        insertRef.current = fn;
      },
      focus: () => inputRef.current?.focus(),
    }),
    [],
  );

  const shell = useMemo<ShellApi>(
    () => ({
      openConversation,
      view: layout.view,
      setView: layout.setView,
      toggleDetail: layout.toggleDetail,
      detailOpen: layout.detailOpen,
      width,
      posture,
      single: layout.single,
      composer: { ...actions, replyTo, editing, inputRef, editPrev: editPrev.current },
    }),
    [openConversation, layout.view, layout.setView, layout.toggleDetail, layout.detailOpen, layout.single, width, posture, actions, replyTo, editing],
  );

  useKeyboardLift(inputRef);

  // While the docked info panel slides open or closed (≥1440 px), messenger.css puts the message lines on layers of
  // their own: the chat changes width every frame, and they are then moved instead of drawn again.
  const shellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return undefined;
    const slide = (on: boolean) => (event: TransitionEvent) => {
      if (event.target !== el || event.propertyName !== 'grid-template-columns') return;
      if (on) el.dataset.sliding = '';
      else delete el.dataset.sliding;
    };
    const start = slide(true);
    const stop = slide(false);
    el.addEventListener('transitionrun', start);
    el.addEventListener('transitionend', stop);
    el.addEventListener('transitioncancel', stop);
    return () => {
      el.removeEventListener('transitionrun', start);
      el.removeEventListener('transitionend', stop);
      el.removeEventListener('transitioncancel', stop);
    };
  }, []);

  const modal = env.modal;
  return (
    <ShellContext.Provider value={shell}>
      <ComposerActionsContext.Provider value={actions}>
      <section className="screen on" id="scr-messenger">
        <Titlebar onSearch={() => env.openModal({ kind: 'search' })} />
        <div className={layout.shellClass} id="shell" ref={shellRef} data-view={layout.view} data-nav={layout.back ? 'back' : undefined}>
          <Rail />
          <ConversationList />
          <ChatPane />
          <DetailPanel />
          <div className="scrim-local" onClick={() => layout.toggleDetail(false)} />
        </div>
      </section>
      {modal?.kind === 'people' ? <PeopleModal key={`${modal.mode}-${modal.preselect?.join()}`} mode={modal.mode} preselect={modal.preselect} /> : null}
      {modal?.kind === 'invite' ? (
        <Suspense fallback={null}>
          <InviteModal onClose={env.closeModal} />
        </Suspense>
      ) : null}
      {modal?.kind === 'search' ? <SearchModal scope={modal.scope ?? null} tab={modal.tab} /> : null}
      {modal?.kind === 'settings' ? (
        <Suspense fallback={null}>
          <SettingsModal />
        </Suspense>
      ) : null}
      {modal?.kind === 'servers' ? <ServersModal /> : null}
      {modal?.kind === 'preview' ? <PreviewModal file={modal.file} gallery={modal.gallery} /> : null}
      {modal?.kind === 'forward' ? (
        <Suspense fallback={null}>
          <ForwardModal conversationId={modal.conversationId} messageId={modal.messageId} />
        </Suspense>
      ) : null}
      </ComposerActionsContext.Provider>
    </ShellContext.Provider>
  );
}

